import type { ChatUser } from '../../../types/chat'
import { getAvailabilityCalendarFromSettings } from '@shared/availability/parse'
import { getProviderActivityStatus } from '../../provider-activity/activity-status'
import { isPresenceHidden, resolveUserPresence, seedPresenceBulk } from '../../../stores/presence-store'
import type { CallTargetContext } from '@shared/calls/call-eligibility'

/**
 * Whether a user has configured ANY availability (recurring or one-off blocks).
 *
 * Cross-worker hook: the contactability model treats "no availability set" as
 * always-contactable, but the TARGET user should be nudged to set their hours so
 * callers get the outside-availability warning. The profile/availability surface
 * (owned by another worker) can call this against the current user's
 * `settingsJson` to decide whether to show that "set your availability" prompt —
 * it is intentionally NOT shown on the caller's gate.
 */
export function hasAvailabilitySet(settingsJson: unknown): boolean {
  const calendar = getAvailabilityCalendarFromSettings(settingsJson)
  return calendar.recurringBlocks.length > 0 || calendar.customBlocks.length > 0
}

/**
 * Build status-aware-calling context for a target user from the directory record
 * plus the live presence store.
 *
 * Presence is read through {@link resolveUserPresence} — the EXACT same resolver
 * the avatar presence dot uses — so the gate can never disagree with what the UI
 * shows the caller. We also seed the target's profile presence/activity into the
 * store first, so a target who was never in the top-50 subscription (and thus had
 * no live record) is still evaluated against their own freshest known data rather
 * than being treated as having no presence. With the contactability model an
 * offline reading no longer blocks anyway, but this keeps the gate honest.
 */
export function buildCallTargetContext(
  user: ChatUser,
  opts?: { onActiveCall?: boolean },
): CallTargetContext {
  // Seed/refresh the store from the directory record so the gate and the avatar
  // read the same source even for users outside the live presence subscription.
  seedPresenceBulk([
    {
      userId: user.userId,
      presenceStatus: user.presenceStatus ?? null,
      lastActiveAt: user.lastActiveAt ?? null,
      presenceVisible: user.presenceVisible,
      settingsJson: user.settingsJson,
    },
  ])
  const activity = getProviderActivityStatus(user)
  // Presence you may not see is unknown: never gate (or warn) on it as "offline".
  const presence = isPresenceHidden(user.userId) ? null : resolveUserPresence(user)
  return {
    userId: user.userId,
    displayName: user.displayName,
    presence,
    activityPresetId: activity?.presetId ?? null,
    pauseNotifications: activity?.pauseNotifications ?? false,
    onActiveCall: opts?.onActiveCall ?? false,
    availabilityCalendar: getAvailabilityCalendarFromSettings(user.settingsJson),
  }
}
